import {getNumberFieldValidationMessage, parseNumber} from '@github-ui/memex-field-validators'
import {TextInput} from '@primer/react'
import {type ChangeEvent, type KeyboardEvent, useCallback, useMemo, useRef, useState} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../../constants/labels'
import {commitUpdateProjectItemFieldValueMutation} from '../../../mutations/update-project-item-field-value'
import textStyles from '../../fields/IssueFieldTextToken.module.css'
import type {NumberFieldConfigFragment$key} from './__generated__/NumberFieldConfigFragment.graphql'
import type {NumberFieldFragment$key} from './__generated__/NumberFieldFragment.graphql'
import {FieldWrapper} from './FieldWrapper'
import type {BaseFieldProps} from './Shared'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const NumberFieldFragment = graphql`
  fragment NumberFieldFragment on ProjectV2ItemFieldNumberValue {
    id
    number
    field {
      ...NumberFieldConfigFragment @dangerously_unaliased_fixme
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const NumberFieldConfigFragment = graphql`
  fragment NumberFieldConfigFragment on ProjectV2Field {
    id
    name
  }
`

type NumberFieldProps = BaseFieldProps<NumberFieldConfigFragment$key, NumberFieldFragment$key>

export function NumberField({viewerCanUpdate, itemId, projectId, field, value, onIssueUpdate}: NumberFieldProps) {
  const [showInput, setShowInput] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Text editor is clean while the user hasn't made any changes,
  // prevent navigating away if there are unsaved changes
  const [isClean, setIsClean] = useState<boolean>(true)

  const environment = useRelayEnvironment()

  const fieldData = useFragment(NumberFieldConfigFragment, field)
  const valueData = useFragment(NumberFieldFragment, value)

  const fieldId = fieldData.id
  const fieldName = fieldData.name

  const originalValue = valueData?.number?.toString() ?? ''
  const [currentValue, setCurrentValue] = useState<string>(originalValue)
  const [invalidMessage, setInvalidMessage] = useState<string | undefined>(undefined)
  const [shouldRestoreRowFocus, setShouldRestoreRowFocus] = useState(false)

  const commitCurrentValue = useCallback(() => {
    if (invalidMessage) {
      setCurrentValue(originalValue)
      setInvalidMessage(undefined)
    } else {
      const newValue = currentValue === '' ? null : parseNumber(currentValue)
      commitUpdateProjectItemFieldValueMutation({
        environment,
        input: {
          fieldId,
          itemId,
          projectId,
          value: {number: newValue},
        },
        onCompleted: onIssueUpdate,
      })
    }
  }, [currentValue, environment, fieldId, invalidMessage, itemId, onIssueUpdate, originalValue, projectId])

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setInvalidMessage(getNumberFieldValidationMessage(newValue))
      setCurrentValue(newValue)
      if (newValue === originalValue) {
        setIsClean(true)
      } else {
        setIsClean(false)
      }
    },
    [originalValue],
  )

  const onUpdate = useCallback(() => {
    commitCurrentValue()
    setShowInput(false)
    setIsClean(true)
    setShouldRestoreRowFocus(true)
  }, [commitCurrentValue])

  const onCancel = useCallback(() => {
    if (inputRef.current) {
      setCurrentValue(originalValue)
      // eslint-disable-next-line github/no-blur
      inputRef.current.blur()
      setShowInput(false)
      setIsClean(true)
      setShouldRestoreRowFocus(true)
    }
  }, [originalValue])

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.key === 'Enter') {
        // We need to stop this as the key events sometimes are handled outside React’s normal event pipeline,
        // so the component doesn’t re-render in time, and we cannot restore the focus to the button. This doesn't
        // have any negative side effects as we handle form submission carefully.
        e.preventDefault()
        onUpdate()
      }
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.key === 'Escape') {
        e.stopPropagation()
        onCancel()
      }
    },
    [onCancel, onUpdate],
  )

  const preventSubmit = useMemo<boolean>(() => !!invalidMessage, [invalidMessage])

  if (!viewerCanUpdate)
    return (
      <FieldWrapper
        showInput={showInput}
        setShowInput={setShowInput}
        placeholder={LABELS.emptySections.noValue(fieldName)}
        canUpdate={false}
        value={currentValue}
        name={fieldName}
      />
    )

  return (
    <FieldWrapper
      showInput={showInput}
      setShowInput={setShowInput}
      value={currentValue}
      name={fieldName}
      placeholder={LABELS.emptySections.numberPlaceholder}
      onCommit={commitCurrentValue}
      validationMessage={invalidMessage}
      preventSubmit={preventSubmit}
      isClean={isClean}
      setIsClean={setIsClean}
      shouldRestoreRowFocus={shouldRestoreRowFocus}
      setShouldRestoreRowFocus={setShouldRestoreRowFocus}
      input={
        <TextInput
          data-testid="number-field-input"
          placeholder={LABELS.emptySections.numberPlaceholder}
          ref={inputRef}
          size="small"
          onChange={onChange}
          onKeyDown={onKeyDown}
          value={currentValue}
          className={textStyles.issueFieldTextInput}
          onFocus={event => event.target.setSelectionRange(event.target.value.length, event.target.value.length)}
          inputMode="numeric"
          autoFocus
        />
      }
    />
  )
}
